import React, { useEffect, useRef, useState } from 'react';
import { Folder, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import { GeneratedImage } from '@/types';
import DownloadButton from '../DownloadButton';
import { resolveImageUrl } from './utils';
import { useTranslation } from '@/i18n/useTranslation';
import { GalleryNoImagesState } from './GalleryEmptyState';

interface GalleryGridViewProps {
  images: GeneratedImage[];
  uuid: string | undefined;
  currentImageIndex: number;
  onSelect: (index: number, switchToSingle: boolean) => void;
  onDelete: (image: GeneratedImage, e?: React.MouseEvent) => void;
  totalCount: number | null;
  loadingMore: boolean;
  onLoadMore: () => void;
  isDesktop: boolean;
  infoPanelVisible: boolean;
  isAuthenticated: boolean;
  onNavigateCreate: () => void;
  onOpenSession: (sessionId: string, imageStyle: string) => void;
}

function GalleryImageCard({ 
  image, 
  index, 
  isSelected, 
  onSelect, 
  onDelete 
}: { 
  image: GeneratedImage; 
  index: number; 
  isSelected: boolean; 
  onSelect: (index: number, switchToSingle: boolean) => void; 
  onDelete: (image: GeneratedImage, e?: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const handleSelect = () => onSelect(index, true);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${
        isSelected ? 'border-accent shadow-lg shadow-accent/20' : 'border-border hover:border-accent/50'
      }`}
    >
      {/* Skeleton loader */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-bg-control animate-pulse" />
      )}
      
      {/* Image */}
      <img
        src={image.url}
        alt={image.filename}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          if (img.dataset.retried === 'true') {
            setImageLoaded(true); // Show even if error to hide skeleton
            return;
          }
          img.dataset.retried = 'true';
          if (!img.src.startsWith('http')) {
            img.src = resolveImageUrl(image.url);
          }
        }}
      />
      
      {/* Hover overlay with buttons */}
      <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-50">
        <DownloadButton imageUrl={image.url} filename={image.filename} size="sm" />
        <button
          type="button"
          className="w-8 h-8 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg text-white hover:bg-black/80 transition-all flex items-center justify-center"
          title={t('button.delete')}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(image, e);
          }}
        >
          <Trash2 className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

function GallerySessionFolderCard({
  sessionId,
  sessionImages,
  onOpenSession
}: {
  sessionId: string;
  sessionImages: GeneratedImage[];
  onOpenSession: (sessionId: string, imageStyle: string) => void;
}) {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const coverImage = sessionImages[sessionImages.length - 1];
  const sessionImageStyle = coverImage?.params?.imageStyle || 'photo';

  return (
    <button
      onClick={() => onOpenSession(sessionId, sessionImageStyle)}
      className="relative aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-accent/50 transition-all group"
      title={t('gallery.openSession')}
    >
      {!imageLoaded && (
        <div className="absolute inset-0 bg-bg-control animate-pulse" />
      )}

      <img
        src={coverImage.url}
        alt={`${t('gallery.session')} ${sessionId}`}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setImageLoaded(true)}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          if (img.dataset.retried === 'true') {
            setImageLoaded(true);
            return;
          }
          img.dataset.retried = 'true';
          if (!img.src.startsWith('http')) {
            img.src = resolveImageUrl(coverImage.url);
          }
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
      <div className="absolute top-2 left-2 w-8 h-8 rounded-lg bg-black/55 border border-white/20 flex items-center justify-center">
        <Folder className="w-4 h-4 text-white" strokeWidth={2} />
      </div>
      <div className="absolute bottom-2 left-2 right-2 text-left">
        <p className="text-xs font-semibold text-white truncate">{t('gallery.session')}</p>
        <p className="text-[11px] text-white/80">{sessionImages.length} images</p>
      </div>
    </button>
  );
}

export default function GalleryGridView({
  images,
  uuid,
  currentImageIndex,
  onSelect,
  onDelete,
  totalCount,
  loadingMore,
  onLoadMore,
  isDesktop,
  infoPanelVisible,
  isAuthenticated,
  onNavigateCreate,
  onOpenSession,
}: GalleryGridViewProps) {
  const { t } = useTranslation();
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

  const groupedBySession = new Map<string, GeneratedImage[]>();
  const ungroupedImages: GeneratedImage[] = [];

  images.forEach((image) => {
    const sessionId = image.params?.designSessionId;
    if (!sessionId) {
      ungroupedImages.push(image);
      return;
    }
    const existing = groupedBySession.get(sessionId) || [];
    existing.push(image);
    groupedBySession.set(sessionId, existing);
  });

  const displayItems: Array<
    { kind: 'folder'; sessionId: string; sessionImages: GeneratedImage[] } |
    { kind: 'image'; image: GeneratedImage; index: number }
  > = [];

  groupedBySession.forEach((sessionImages, sessionId) => {
    if (sessionImages.length >= 2) {
      const sorted = [...sessionImages].sort((a, b) => a.timestamp - b.timestamp);
      displayItems.push({ kind: 'folder', sessionId, sessionImages: sorted });
    } else {
      const singleImage = sessionImages[0];
      const originalIndex = images.indexOf(singleImage);
      displayItems.push({ kind: 'image', image: singleImage, index: originalIndex });
    }
  });

  ungroupedImages.forEach((image) => {
    const originalIndex = images.indexOf(image);
    displayItems.push({ kind: 'image', image, index: originalIndex });
  });

  displayItems.sort((a, b) => {
    const aTime = a.kind === 'folder'
      ? a.sessionImages[a.sessionImages.length - 1]?.timestamp || 0
      : a.image.timestamp;
    const bTime = b.kind === 'folder'
      ? b.sessionImages[b.sessionImages.length - 1]?.timestamp || 0
      : b.image.timestamp;
    return bTime - aTime;
  });

  if (images.length === 0) {
    return (
      <div
        className="gallery-grid flex-1 overflow-y-auto p-4 md:p-6"
        style={{
          marginRight: isDesktop && infoPanelVisible ? '300px' : '0',
          transition: 'margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <GalleryNoImagesState
          isAuthenticated={isAuthenticated}
          onNavigateCreate={onNavigateCreate}
        />
      </div>
    );
  }

  const hasMore = totalCount === null || images.length < totalCount;

  useEffect(() => {
    if (!hasMore || loadingMore) return;
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '300px 0px',
        threshold: 0.01
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, images.length]);

  return (
    <div
      className="gallery-grid flex-1 overflow-y-auto p-4 md:p-6"
      style={{
        marginRight: isDesktop && infoPanelVisible ? '300px' : '0',
        transition: 'margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 md:gap-4 auto-rows-fr">
        {displayItems.map((item) => {
          if (item.kind === 'folder') {
            return (
              <GallerySessionFolderCard
                key={`session-folder-${item.sessionId}`}
                sessionId={item.sessionId}
                sessionImages={item.sessionImages}
                onOpenSession={onOpenSession}
              />
            );
          }

          const { image, index } = item;
          const safeIndex = index >= 0 ? index : 0;
          const isSelected = uuid ? image.id === uuid : safeIndex === currentImageIndex;
          return (
            <GalleryImageCard
              key={image.id || image.timestamp}
              image={image}
              index={safeIndex}
              isSelected={isSelected}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          );
        })}
        {hasMore && (
          <div ref={loadMoreSentinelRef} className="col-span-full h-16 flex items-center justify-center">
            {loadingMore ? (
              <Loader2 className="w-6 h-6 text-accent animate-spin" strokeWidth={2} />
            ) : (
              <span className="text-xs text-text-secondary">{t('gallery.loadMore')}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
